import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import {clearSelection} from 'src/components/data-table-selection';
import {selectDataTableSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {ACCOUNT_TABS} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {AssignAccountEntitlementsTool} from 'src/modules/bulk-update';
import {NoResultPage, PreparedEntitlementsView, ResourceSuggest, useExpandRoles} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import StateButton from '@pb/kit-component-state-button';
import {StickyElement} from '@pb/kit-component-sticky-element';


import {AssignAccountEntitlementsPagination} from './assign-account-entitlements-pagination';
import {ResourceNameHeader} from './components/respource-name.header';
import {
    NO_ASSIGNED_ENTITLEMENT_DESCRIPTION,
    NO_ASSIGNED_ENTITLEMENT_TITLE,
    SELECTION_KEY,
} from './constants';
import {useGetAllEntitlements} from './hooks/useGetAllEntitlements';
import {assignAccountEntitlementsStateActions} from './slices/assign-account-entitlements-state.slice';
import {assignAccountEntitlementsActions} from './slices/assign-account-entitlements.slice';

import {
    assignAccountEntitlementsControlHeader,
    assignAccountEntitlementsControlHeaderSticky,
    assignAccountEntitlementsLoadingContainer,
    assignAccountEntitlementsTools,
} from './assign-account-entitlements.module.styl';

import type {AppState} from 'src/store/typedef';

export const AssignAccountEntitlements = () => {
    const dispatch = useDispatch();
    const queryParams = useParams<{accountId: string}>();
    const accountId = queryParams.accountId ? +queryParams.accountId : -1;
    const navigate = useNavigate();

    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();
    const {loadingError, result, cacheKeys} = useGetAllEntitlements();
    const selection = useSelector((state: AppState) => selectDataTableSelection(state, SELECTION_KEY));

    const {
        isExpanded,
        disabled: isRoleExpansionDisabled,
        toggleRolesExpansion,
        expandedRoleIds,
        setExpandedRoleIds,
    } = useExpandRoles({records: result?.data || []});

    const {setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'assign-account-entitlements',
        onProceed: () => {
            navigate(`/profile/account/${accountId}/${ACCOUNT_TABS.ENTITLEMENTS.name}`);
        },
    });

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(assignAccountEntitlementsActions.clearData());
            dispatch(assignAccountEntitlementsStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prev, current) => {
        if (
            !isDeepEqual({prev: prev.selection, current: current.selection}) ||
            !isDeepEqual({prev: prev.result, current: current.result})
        ) {
            if (
                (selection.inverted && selection.ids.length !== result?.totalCount) ||
                selection.ids.length
            ) {
                setIsChunkChanged(true);
            } else {
                setIsChunkChanged(false);
            }
        }
    }, {selection, result});

    const filterResources = useCallback((resourceIds: string[] | null, query: string) => {
        dispatch(assignAccountEntitlementsStateActions.setFilters(resourceIds ? {
            resourceIds,
            resourceQuery: query,
        } : {}));
    }, [dispatch]);

    const noResultMessage = useMemo(() => {
        const filteredResources = cacheKeys.filters.resourceIds as string[] | undefined;
        if (filteredResources && !filteredResources.length) {
            return (
                <NoResultPage
                    title="No resources found for the selected criteria."
                    description=""
                />
            );
        }

        if (result && !result.data.length) {
            return (
                <NoResultPage
                    title={NO_ASSIGNED_ENTITLEMENT_TITLE}
                    description={NO_ASSIGNED_ENTITLEMENT_DESCRIPTION}
                />
            );
        }

        return <div/>;
    }, [cacheKeys.filters, result]);

    return (
        <ServerResponseHandler serverError={loadingError} sectionName="Assign Account Entitlements">
            <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                <StickyElement className={assignAccountEntitlementsControlHeaderSticky}>
                    <div className={assignAccountEntitlementsControlHeader}>
                        <Group justify="space-between" alignItems="center">
                            <ResourceSuggest onSearch={filterResources} id="resources-suggest"/>
                            <Group alignItems="center">
                                <Label>Expand Roles</Label>
                                <StateButton
                                    {...{
                                        name: 'expand-roles',
                                        disabled: isRoleExpansionDisabled,
                                        checked: isExpanded,
                                        onChange: toggleRolesExpansion,
                                    }}
                                />
                                <AssignAccountEntitlementsTool
                                    selectionKey={SELECTION_KEY}
                                    accountId={accountId}
                                    onDone={() => {
                                        dispatch(clearSelection(SELECTION_KEY));
                                        dispatch(assignAccountEntitlementsActions.clearData());
                                        proceed();
                                    }}
                                />
                            </Group>
                        </Group>
                    </div>
                </StickyElement>
                <BoxLoading loading={!result}>
                    <div className={assignAccountEntitlementsLoadingContainer}>
                        {!!result?.data.length && (
                            <PreparedEntitlementsView
                                dataTableId="assign-account-entitlements"
                                rows={result.data}
                                selectionKey={SELECTION_KEY}
                                toolRibbonClassName={assignAccountEntitlementsTools}
                                customOffset={137}
                                expandedRoleIds={expandedRoleIds}
                                setExpandedRoleIds={setExpandedRoleIds}
                                getColumnWidthByColumnId={getColumnWidthByColumnId}
                                onStopResize={onStopResize}
                                paginationStripe={<AssignAccountEntitlementsPagination totalCount={result.totalCount}/>}
                                shortHeader={<ResourceNameHeader/>}
                                resourceFilter={(cacheKeys.filters.resourceQuery as string | undefined) || ''}
                            />
                        )}
                        {noResultMessage}
                    </div>
                </BoxLoading>
            </ErrorBoundary>
        </ServerResponseHandler>
    );
};
